import React, { useState, useEffect, useRef } from 'react';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { useSystemFeatures } from '../hooks/useSystemFeatures';
import { splitTaxInclusive, taxLabel } from '../utils/systemFeatures';

function OrderSummary({ orderId, orderItems = [], tableName, orderName, parentOrderName, allOptions, theme, user, printRef, layoutMode = 'mobile' }) {
    const { t, language } = useLanguage();
    const { taxRate } = useSystemFeatures();

    // Specials state
    // eslint-disable-next-line no-unused-vars
    const [applicableSpecials, setApplicableSpecials] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [checkingSpecials, setCheckingSpecials] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [selectedSpecial, setSelectedSpecial] = useState(null);
    const [specialDiscount, setSpecialDiscount] = useState(0);
    const [orderData, setOrderData] = useState(null);

    // Print state
    // eslint-disable-next-line no-unused-vars
    const [showPrintTicket, setShowPrintTicket] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [printLoading, setPrintLoading] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [showIosBluetoothHelp, setShowIosBluetoothHelp] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [showPrinterMapping, setShowPrinterMapping] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [printerMappings, setPrinterMappings] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [skipPrinterMapping, setSkipPrinterMapping] = useState(
        localStorage.getItem('skipPrinterMapping') === 'true'
    );
    const printerMappingResolveRef = useRef(null);

    // Fetch order data to get special name
    useEffect(() => {
        const fetchOrderData = async () => {
            if (!orderId) return;

            try {
                const response = await fetch(`${BACKEND_URL}/api/orders/${orderId}`);
                if (response.ok) {
                    const data = await response.json();
                    setOrderData(data);
                }
            } catch (err) {
                console.error('Error fetching order data:', err);
            }
        };

        fetchOrderData();
    }, [orderId]);

    // Check for applicable specials when order items change
    useEffect(() => {
        if (orderItems.length > 0) {
            checkApplicableSpecials();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderItems]);

    // Calculate totals
    const subtotalPrice = orderItems.reduce((sum, item) => {
        return sum + (Number(item.price || 0) * item.quantity);
    }, 0);

    const itemDiscount = orderItems.reduce((sum, item) => {
        return sum + (Number(item.discount || 0) * item.quantity);
    }, 0);

    const totalDiscount = itemDiscount + specialDiscount;
    const totalPrice = subtotalPrice - totalDiscount;

    // Count in-progress items (status 0, 1, or 2)
    const inProgressCount = orderItems.filter(item => [0, 1, 2].includes(item.status)).length;

    // Helper function to get description in current language
    // eslint-disable-next-line no-unused-vars
    const getSpecialDescription = (special) => {
        if (language === 'es' && special.description_español) {
            return special.description_español;
        }
        return special.description;
    };

    // Check for applicable specials
    const checkApplicableSpecials = async () => {
        setCheckingSpecials(true);
        try {
            const response = await fetch(`${BACKEND_URL}/api/specials/check-applicable`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ order_items: orderItems })
            });

            if (response.ok) {
                const specials = await response.json();
                setApplicableSpecials(specials);
            }
        } catch (err) {
            console.error('Error checking specials:', err);
        } finally {
            setCheckingSpecials(false);
        }
    };

    // Apply a special discount
    // eslint-disable-next-line no-unused-vars
    const applySpecial = (special) => {
        if (special.special_type === 'combo_deal') {
            const comboItemIds = (special.matching_items || []).map(i => i.id);
            const comboDiscount = special.combo_discount || (special.combo_total - special.combo_price);
            fetch(`${BACKEND_URL}/api/orders/${orderId}/apply-combo-discount`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    combo_special_id: special.id,
                    combo_discount: comboDiscount,
                    combo_item_ids: comboItemIds
                })
            })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setSelectedSpecial(special);
                    setSpecialDiscount(comboDiscount);
                    // Trigger parent to refresh order items
                    window.dispatchEvent(new CustomEvent('refreshOrderItems'));
                } else {
                    alert(t('error') + ': ' + (data.error || 'Failed to apply combo discount'));
                }
            })
            .catch(err => {
                console.error('Error applying combo discount:', err);
                alert(t('error') + ': ' + err.message);
            });
        } else if (
            special.special_type === 'buy_x_get_y_free' ||
            special.special_type === 'percentage_discount' ||
            special.special_type === 'fixed_discount'
        ) {
            let matchingItemIds;
            if (special.special_type === 'buy_x_get_y_free') {
                const excludedOptionIds = Array.isArray(special.conditions?.exclude_option_ids)
                    ? special.conditions.exclude_option_ids.map(Number)
                    : [];
                matchingItemIds = (special.matching_items || [])
                    .filter(i => {
                        let optionIds = [];
                        if (!i.options) return true;
                        if (typeof i.options === 'string') {
                            try { optionIds = JSON.parse(i.options); } catch { return true; }
                        } else if (Array.isArray(i.options)) {
                            optionIds = i.options;
                        }
                        if (!optionIds || optionIds.length === 0) return true;
                        return !optionIds.some(optId => excludedOptionIds.includes(Number(optId)));
                    })
                    .map(i => i.id);
            } else {
                matchingItemIds = (special.matching_items || []).map(i => i.id);
            }
            let payload = {
                special_id: special.id,
                special_type: special.special_type,
                matching_item_ids: matchingItemIds
            };
            if (special.special_type === 'buy_x_get_y_free') {
                payload.buy_quantity = special.conditions?.buy_quantity || 0;
                payload.get_quantity = special.conditions?.get_quantity || 0;
            } else {
                payload.discount_value = special.discount_value;
            }
            fetch(`${BACKEND_URL}/api/orders/${orderId}/apply-special-discount`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setSelectedSpecial(special);
                    setSpecialDiscount(data.total_discount || 0);
                    // Trigger parent to refresh order items
                    window.dispatchEvent(new CustomEvent('refreshOrderItems'));
                } else {
                    alert(t('error') + ': ' + (data.error || 'Failed to apply special discount'));
                }
            })
            .catch(err => {
                console.error('Error applying special discount:', err);
                alert(t('error') + ': ' + err.message);
            });
        }
    };

    // Remove applied special
    // eslint-disable-next-line no-unused-vars
    const removeSpecial = () => {
        fetch(`${BACKEND_URL}/api/orders/${orderId}/clear-discounts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        })
        .then(res => res.json())
        .then(data => {
            if (data.success) {
                setSelectedSpecial(null);
                setSpecialDiscount(0);
                // Trigger parent to refresh order items
                window.dispatchEvent(new CustomEvent('refreshOrderItems'));
            } else {
                alert(t('error') + ': ' + (data.error || 'Failed to clear discounts'));
            }
        })
        .catch(err => {
            console.error('Error clearing discounts:', err);
            alert(t('error') + ': ' + err.message);
        });
    };

    // eslint-disable-next-line no-unused-vars
    const handlePrint = async () => {
        setPrintLoading(true);

        try {
            // Load printer settings
            let settings = null;
            try {
                const response = await fetch(`${BACKEND_URL}/api/printer-settings`);
                if (response.ok) {
                    settings = await response.json();
                } else {
                    console.warn('Could not load settings from API, using localStorage fallback');
                }
            } catch (err) {
                console.warn('Error fetching settings from API:', err);
            }

            // Fallback to localStorage
            if (!settings) {
                settings = {};
                try {
                    const savedConfig = localStorage.getItem('printerConfig');
                    if (savedConfig) {
                        const config = JSON.parse(savedConfig);
                        settings.paperWidth = config.width || 32;
                        settings.fontSize = config.fontSize || 'normal';
                    }
                    const savedFormat = localStorage.getItem('receiptFormat');
                    if (savedFormat) {
                        const format = JSON.parse(savedFormat);
                        Object.assign(settings, format);
                    }
                } catch (localErr) {
                    console.warn('Could not load from localStorage:', localErr);
                }
            }

            // Generate receipt
            const receipt = generateReceiptForBluetooth(orderItems, orderId, tableName, orderName, orderData?.special_name, settings, orderData?.closed_at);

            // Print via Bluetooth
            await printViaBluetooth(receipt);

            console.log('Receipt printed successfully via Bluetooth');
        } catch (error) {
            console.error('Bluetooth print error:', error);

            if (error.message !== 'iOS_BLUETOOTH_NOT_SUPPORTED') {
                let errorMessage = error.message;
                if (error.name === 'NotFoundError') {
                    errorMessage = t('noPrinterSelected');
                } else if (error.name === 'SecurityError') {
                    errorMessage = t('accessDenied');
                } else if (error.name === 'NetworkError') {
                    errorMessage = t('connectionFailed');
                }

                alert(t('printError') + ': ' + errorMessage);
            }
        } finally {
            setPrintLoading(false);
        }
    };

    // Generate ESC/POS receipt
    const generateReceiptForBluetooth = (orderItems, orderId, tableName, orderName, specialName, settings, closedAt) => {
        const width = settings?.paperWidth || 32;
        const fontSize = settings?.fontSize || 'normal';
        const businessName = settings?.businessName || 'CERVECERIA GIBBONS';
        const address = settings?.address || 'C. ESCORZA 848';
        const taxId = settings?.taxId || 'CGI181112777';
        const footerMessage1 = settings?.footerMessage1 || '';
        const footerMessage2 = settings?.footerMessage2 || '';
        const footerMessage3 = settings?.footerMessage3 || '';
        const includeIva = settings?.includeIva !== false;
        const paperCut = settings?.paperCut || 'partial';

        const separator = '='.repeat(width);
        const dashLine = '-'.repeat(width);

        const leftRight = (left, right, width) => {
            const spaces = Math.max(1, width - left.length - right.length);
            return left + ' '.repeat(spaces) + right;
        };

        let receipt = '\x1B\x40'; // Initialize printer
        receipt += '\x1B\x74\x00'; // Set character code table

        // Font size
        if (fontSize === 'small') {
            receipt += '\x1D\x21\x00';
        } else if (fontSize === 'large') {
            receipt += '\x1D\x21\x11';
        } else {
            receipt += '\x1D\x21\x01';
        }

        // Header
        receipt += '\x1B\x61\x01'; // Center align
        receipt += '\x1B\x45\x01'; // Bold on
        receipt += businessName + '\n';
        receipt += '\x1B\x45\x00'; // Bold off
        receipt += address + '\n';
        receipt += taxId + '\n';
        receipt += '\x1B\x61\x00'; // Left align
        receipt += separator + '\n\n';

        // Order info — use closedAt for closed orders, current time for active
        const now = closedAt ? new Date(closedAt) : new Date();
        const dateTime = now.toLocaleString('es-MX', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });

        receipt += `Fecha/Hora: ${dateTime}\n`;
        receipt += `Orden #: ${orderId}\n`;
        receipt += `Mesa: ${tableName || 'N/A'}\n`;
        if (orderName && orderName !== tableName) {
            receipt += `Nombre: ${orderName}\n`;
        }

        // Special/promotion section (if applied)
        if (specialName) {
            receipt += '\x1B\x61\x01'; // Center align
            receipt += '\x1B\x45\x01'; // Bold on
            receipt += '\x1B\x21\x08'; // Double strike
            receipt += '\n--- PROMOCION APLICADA ---\n';
            receipt += `${specialName}\n`;
            receipt += '\x1B\x45\x00'; // Bold off
            receipt += '\x1B\x21\x00'; // Normal
            receipt += '\x1B\x61\x00'; // Left align
        }
        receipt += '\n';

        // Items header
        receipt += dashLine + '\n';
        receipt += 'CANT PRODUCTO             PRECIO\n';
        receipt += dashLine + '\n';

        // Calculate total
        let total = 0;
        let totalDiscount = 0;

        // Items
        orderItems.forEach(item => {
            const qty = item.quantity;
            const name = item.product_name;
            const price = Number(item.price || 0);
            const discount = Number(item.discount || 0);
            const itemTotal = qty * (price - discount);
            total += itemTotal;

            const qtyStr = qty.toString().padStart(2);
            const maxNameLength = width - 14;
            const truncatedName = name.length > maxNameLength
                ? name.substring(0, maxNameLength - 3) + '...'
                : name;
            const priceStr = `$${itemTotal.toFixed(2)}`.padStart(8);
            receipt += `${qtyStr}x ${truncatedName.padEnd(maxNameLength)}${priceStr}\n`;

            if (discount > 0) {
                const lineDiscount = discount * qty;
                totalDiscount += lineDiscount;
                receipt += `    (Descuento: -$${lineDiscount.toFixed(2)})\n`;
            }
        });

        // Totals
        receipt += dashLine + '\n';

        // Show total discount if there is any
        if (totalDiscount > 0) {
            receipt += '\x1B\x45\x01'; // Bold on
            receipt += '\x1B\x21\x08'; // Double strike
            receipt += leftRight('Descuento:', `-$${totalDiscount.toFixed(2)}`, width) + '\n';
            receipt += '\x1B\x45\x00'; // Bold off
            receipt += '\x1B\x21\x00'; // Normal
            receipt += '\n'; // Blank line after discount
        }

        if (includeIva) {
            const { subtotal, tax: ivaAmount } = splitTaxInclusive(total, taxRate);
            receipt += leftRight('Subtotal:', `$${subtotal.toFixed(2)}`, width) + '\n';
            receipt += leftRight(`${taxLabel(taxRate)}:`, `$${ivaAmount.toFixed(2)}`, width) + '\n';
        }
        receipt += '\x1B\x45\x01'; // Bold on
        receipt += leftRight('TOTAL:', `$${total.toFixed(2)}`, width) + '\n';
        receipt += separator + '\n\n';
        receipt += '\x1B\x45\x00'; // Bold off

        // Footer
        receipt += '\x1B\x61\x01'; // Center align
        if (footerMessage1) receipt += footerMessage1 + '\n';
        if (footerMessage2) receipt += footerMessage2 + '\n';
        if (footerMessage1 || footerMessage2) receipt += '\n';
        if (footerMessage3) receipt += footerMessage3 + '\n';
        receipt += '\x1B\x61\x00'; // Left align
        receipt += separator + '\n';

        // Feed and cut
        receipt += '\n\n\n\n';

        if (paperCut === 'full') {
            receipt += '\x1D\x56\x00';
        } else if (paperCut === 'partial') {
            receipt += '\x1D\x56\x41\x03';
        }

        return receipt;
    };

    // Print via Bluetooth
    const printViaBluetooth = async (receipt) => {
        if (!('bluetooth' in navigator)) {
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
            if (isIOS) {
                setShowIosBluetoothHelp(true);
                throw new Error('iOS_BLUETOOTH_NOT_SUPPORTED');
            }
            throw new Error('Web Bluetooth API not supported. Please use Chrome, Edge, or Opera browser (version 89+) on a desktop or Android device.');
        }

        // Load saved printers
        let savedPrinters = [];
        try {
            const response = await fetch(`${BACKEND_URL}/api/bluetooth-printers`);
            if (response.ok) {
                savedPrinters = await response.json();
            }
        } catch (error) {
            console.warn('Could not load saved printers, using default filters:', error);
        }

        // Show printer mapping modal if needed
        if (savedPrinters.length > 0 && !skipPrinterMapping) {
            setPrinterMappings(savedPrinters);
            await new Promise((resolve) => {
                printerMappingResolveRef.current = resolve;
                setShowPrinterMapping(true);
            });
        }

        // Build filters
        const filters = [
            { services: ['000018f0-0000-1000-8000-00805f9b34fb'] },
        ];

        savedPrinters.forEach(printer => {
            filters.push({ name: printer.device_name });
        });

        filters.push(
            { name: 'GOOJPRT' },
            { namePrefix: 'PT' },
            { namePrefix: 'Printer' }
        );

        // Request Bluetooth device
        const device = await navigator.bluetooth.requestDevice({
            filters: filters,
            optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb']
        });

        // Connect and print
        const server = await device.gatt.connect();
        const service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb');
        const characteristic = await service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb');

        const encoder = new TextEncoder();
        const data = encoder.encode(receipt);

        const chunkSize = 20;
        for (let i = 0; i < data.length; i += chunkSize) {
            const chunk = data.slice(i, i + chunkSize);
            await characteristic.writeValue(chunk);
            await new Promise(resolve => setTimeout(resolve, 10));
        }

        device.gatt.disconnect();
    };

    return (
        <div style={{
            padding: '0.75rem 1rem',
            backgroundColor: theme?.colors?.surface || '#fff',
            borderRadius: theme?.borderRadius || 8,
            boxShadow: theme?.boxShadow,
            marginBottom: 0,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '1rem',
            minWidth: 0,
            flexWrap: 'nowrap',
            whiteSpace: 'nowrap'
        }}>
            <div style={{
                color: theme?.colors?.text || '#222',
                fontSize: '1rem',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
            }}>
                {t('total')} {t('items')} ({orderItems.length}) - {t('inProgress')} ({inProgressCount})
            </div>
            <div style={{
                color: theme?.colors?.primary || '#1976d2',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                flexShrink: 0
            }}>
                {t('total')}: ${totalPrice.toFixed(2)}
            </div>
        </div>
    );
}

export default OrderSummary;
